import { useMemo, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Mui
import Box from "@mui/material/Box";

// Components
import ProjectionHeader from "./ProjectionHeader";
import ProjectionBody from "./ProjectionBody";
import { ChartErrorMessage } from "components/ChartErrorMessage";

// Styles
import useStyles from "./Projection.styles";

// Hooks
import useGraphsFetch from "utils/hooks/useGraphsFetch";
import { useAppSelector, useAppDispatch } from "app/hooks";

// Store
import { getCharts, fetchCharts } from "store/slice/charts";
import { getLoaderState, showLoader, toggleLoader } from "store/slice/UI";
import { fetchPersonSet } from "store/slice/Project/peopleSet/peopleSetSlice";
import { fetchCompanySet } from "store/slice/Project/companiesSet/companiesSetSlice";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { getPersonSetByPath, getPeopleSets } from "store/slice/Project/peopleSet/peopleSetSlice";
import { getCompanySetByPath, getCompanySets } from "store/slice/Project/companiesSet/companiesSetSlice";
import { fetchProjectCompany, getProjectCompanyData } from "store/slice/Project/projectData/ProjectSlice";

// Enums
import { urlPaths } from "enums/urlPaths";

// Utils
import { isEmpty } from "lodash";
import { shallowEqual } from "react-redux";
import { useRedirectOnProjectError } from "utils/hooks";
import {
    getInitialFetchResponse,
    getInsightPeersPath,
    getInsightRelatedSltPath,
} from "pages/ProjectDashboard/adapters/formattedData";

const Projection = (): JSX.Element => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Url params
    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([companyId, projectId, insightId]);

    // Data
    const { status: sltSetStatus } = useAppSelector(getPeopleSets);
    const { status: peersSetStatus } = useAppSelector(getCompanySets);
    const { data: company, status: companyStatus } = useAppSelector(getProjectCompanyData);
    const { data: chartData, status: chartStatus } = useAppSelector(getCharts);
    const { data: insightData, status: insightStatus } = useAppSelector(
        getInsightById(Number(insightId)),
        shallowEqual
    );

    // Insight's Peers and SLT
    const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;
    const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;
    const peerIds = useAppSelector(getCompanySetByPath(relatedPeersPath), shallowEqual)?.data?.company_ids;
    const sltIds = useAppSelector(getPersonSetByPath(relatedSltPath), shallowEqual)?.data?.person_ids;

    // Data for graph fetch
    const graphData = useGraphsFetch();
    const { target_company_board: selectedBoard } = graphData;
    const hasDataForRequest = !!companyId && peerIds !== undefined && selectedBoard.length;

    // Other flags
    const isLoaderVisible = useAppSelector(getLoaderState);
    const noBoardMembers = Boolean(sltIds && sltSetStatus !== "fetching" && !selectedBoard.length);
    const hasChartData = useMemo(() => {
        return (
            !isEmpty(chartData) &&
            Object.values(chartData?.graphs || {}).every((chart) => chart.raw !== null && !isEmpty(chart.raw))
        );
    }, [chartData]);

    // Fetch Project Data
    useEffect(() => {
        const hasCompanyData = !isEmpty(company);
        const hasInsightData = !isEmpty(insightData);

        const initialFetch = async () => {
            const promises = [];

            if (!hasCompanyData && companyStatus === "pristine" && companyId) {
                promises.push(dispatch(fetchProjectCompany(companyId)));
            }

            if (!hasInsightData && insightStatus !== "fetching" && projectId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (promises.length && projectId) {
                dispatch(toggleLoader(true));

                const response = await Promise.allSettled(promises);
                const result = getInitialFetchResponse(response);

                if (!("error" in result)) {
                    await fetchLists();
                }

                if ("error" in result) {
                    console.error(result.reason);
                    navigate(urlPaths.NotFound);
                }
            }
        };

        const fetchLists = async () => {
            !isLoaderVisible && dispatch(showLoader(true));

            const promises = [];

            if (relatedSltPath && projectId && sltSetStatus !== "fetching") {
                promises.push(dispatch(fetchPersonSet({ projectId, path: relatedSltPath })));
            }

            if (relatedPeersPath && projectId && peersSetStatus !== "fetching") {
                promises.push(dispatch(fetchCompanySet({ projectId, path: relatedPeersPath })));
            }

            await Promise.allSettled(promises);
        };

        const fetchAllCharts = async () => {
            if (projectId && insightId) {
                try {
                    !isLoaderVisible && dispatch(showLoader(true));
                    await dispatch(fetchCharts({ graphData, projectId, insightId }));
                } catch (error) {
                    console.error(error);
                } finally {
                    dispatch(showLoader(false));
                }
            }
        };

        if (!hasCompanyData && (sltIds === undefined || peerIds === undefined)) {
            initialFetch();
            return;
        }

        if (hasInsightData && !hasDataForRequest) {
            fetchLists();
            return;
        }

        if (hasDataForRequest && !hasChartData && chartStatus === "pristine") {
            fetchAllCharts();
            return;
        }
    }, [
        chartStatus,
        company,
        companyId,
        companyStatus,
        graphData,
        hasChartData,
        hasDataForRequest,
        insightData,
        insightId,
        insightStatus,
        isLoaderVisible,
        peerIds,
        peersSetStatus,
        projectId,
        relatedPeersPath,
        relatedSltPath,
        sltIds,
        sltSetStatus,
        dispatch,
        navigate,
    ]);

    return (
        <Box className={classes.projection}>
            <ProjectionHeader companyData={company} />
            {hasChartData && <ProjectionBody />}
            {noBoardMembers && (
                <Box className={classes.projectionPlaceholder}>
                    <ChartErrorMessage fromInsights />
                </Box>
            )}
        </Box>
    );
};

export default Projection;
